import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Paginator } from 'primereact/paginator';
import { useDebounce } from 'use-debounce';
import CustomToast from '../../components/CustomToast';
import StatusChangeDialog from './StatusChangeDialog';
import {
  PERM,
  APPLICATION_STATUS,
  APPLICATION_STATUS_KEYS,
  apiErrorMessage,
  applicationPath,
  formatDate,
  fromDateOnly,
  saveBlob,
  stars,
  toDateOnly,
  useCareersPermissions,
  useCareersRequest,
} from './careersShared';
import {
  CareersPage, EmptyState, ErrorBanner, Loading, PageHeader, PersonAvatar, SearchBox, StatusBadge,
} from './careersUI';
import './careers.css';

const PAGE_SIZE = 25;

const SORT_OPTIONS = [
  { label: 'Newest first', value: 'newest' },
  { label: 'Oldest first', value: 'oldest' },
  { label: 'Recently updated', value: 'updated' },
  { label: 'Highest rated', value: 'rating' },
  { label: 'Lowest expected CTC', value: 'ctc' },
];

const RATING_OPTIONS = [5, 4, 3, 2, 1].map((n) => ({ label: `${n}★ and above`, value: String(n) }));

// Everything except "applied" can be set in bulk
const BULK_STATUSES = APPLICATION_STATUS_KEYS.filter((key) => key !== 'applied');

// URL query keys used as filters
const FILTER_KEYS = ['job_id', 'status', 'category_id', 'search', 'from', 'to', 'assigned_to', 'min_rating', 'sort'];

const Applications = () => {
  const request = useCareersRequest();
  const can = useCareersPermissions();
  const navigate = useNavigate();
  const toastRef = useRef(null);
  const [params, setParams] = useSearchParams();

  const filters = useMemo(() => Object.fromEntries(FILTER_KEYS.map((key) => [key, params.get(key) || ''])), [params]);
  const page = Math.max(1, parseInt(params.get('page'), 10) || 1);

  const [searchText, setSearchText] = useState(filters.search);
  const [debouncedSearch] = useDebounce(searchText, 350);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selected, setSelected] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  const canManage = can(PERM.manageApplications);

  const notify = (severity, summary, detail) => toastRef.current?.show(severity, summary, detail, 5000);

  /** Update filters in the URL. Any filter change returns to page 1. */
  const setFilters = useCallback((changes) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') next.delete(key);
        else next.set(key, String(value));
      });
      if (!('page' in changes)) next.delete('page');
      return next;
    }, { replace: true });
  }, [setParams]);

  useEffect(() => {
    if (debouncedSearch !== filters.search) setFilters({ search: debouncedSearch.trim() });
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const queryParams = useMemo(() => {
    const query = { page, pageSize: PAGE_SIZE };
    FILTER_KEYS.forEach((key) => { if (filters[key]) query[key] = filters[key]; });
    return query;
  }, [filters, page]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await request('/applications', { params: queryParams });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setSelected([]);
    } catch (error) {
      setLoadError(apiErrorMessage(error, 'Could not load applications.'));
    } finally {
      setLoading(false);
    }
  }, [request, queryParams]);

  const loadStats = useCallback(() => {
    request('/stats').then(setStats).catch(() => setStats(null));
  }, [request]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  useEffect(() => {
    if (can(PERM.view)) request('/categories').then((data) => setCategories(data.categories || [])).catch(() => {});
    if (canManage) request('/assignees').then((data) => setAssignees(data.users || [])).catch(() => {});
  }, [request, can, canManage]);

  const jobOptions = (stats?.jobs || []).map((job) => ({
    label: job.status === 'active' ? job.title : `${job.title} (${job.status || 'deleted'})`,
    value: String(job.job_id),
  }));
  const assigneeOptions = [
    { label: 'Assigned to me', value: 'me' },
    { label: 'Unassigned', value: 'none' },
    ...assignees.map((user) => ({ label: user.name, value: String(user.user_id) })),
  ];
  const dateRange = [fromDateOnly(filters.from), fromDateOnly(filters.to)].filter(Boolean);
  const hasFilters = FILTER_KEYS.some((key) => key !== 'sort' && filters[key]);

  const allOnPageSelected = items.length > 0 && items.every((a) => selected.includes(a.application_id));
  const toggleAll = () => setSelected(allOnPageSelected ? [] : items.map((a) => a.application_id));
  const toggleOne = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const bulkUpdate = async (body) => {
    setBusy(true);
    try {
      const result = await request('/applications/bulk-status', { method: 'post', data: { ids: selected, ...body } });
      setBulkOpen(false);
      const label = APPLICATION_STATUS[body.status].label;
      if (result.skipped?.length) {
        notify('warn', `${result.updated.length} moved to ${label}`, `${result.skipped.length} skipped: ${result.skipped[0].reason}${result.skipped.length > 1 ? ' (and others)' : ''}`);
      } else {
        notify('success', 'Updated', `${result.updated.length} application(s) moved to ${label}.`);
      }
      await Promise.all([load(), loadStats()]);
    } catch (error) {
      notify('error', 'Not updated', apiErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const { page: _page, pageSize: _size, ...exportParams } = queryParams;
      const blob = await request('/applications/export', { params: exportParams, responseType: 'blob' });
      saveBlob(blob, `applications-${toDateOnly(new Date())}.xlsx`);
    } catch (error) {
      notify('error', 'Export failed', apiErrorMessage(error, 'Could not export applications.'));
    } finally {
      setExporting(false);
    }
  };
  return (
    <CareersPage>
      <CustomToast ref={toastRef} />
      <PageHeader
        title="Applications"
        subtitle="Review candidates, move them through the hiring pipeline, and keep notes."
        actions={can(PERM.export) && (
          <Button
            label="Export to Excel"
            icon="pi pi-file-excel"
            severity="success"
            outlined
            loading={exporting}
            disabled={!total}
            onClick={exportExcel}
          />
        )}
      />

      {stats && (
        <>
          <div className="careers-pipeline" role="group" aria-label="Filter by status">
            <button
              type="button"
              className={`careers-stage ${!filters.status ? 'is-active' : ''}`}
              onClick={() => setFilters({ status: '' })}
              aria-pressed={!filters.status}
            >
              <span>All</span>
              <strong>{stats.total}</strong>
            </button>
            {APPLICATION_STATUS_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                className={`careers-stage ${filters.status === key ? 'is-active' : ''}`}
                onClick={() => setFilters({ status: filters.status === key ? '' : key })}
                aria-pressed={filters.status === key}
              >
                <span><i className={`careers-dot is-${key}`} aria-hidden="true" />{APPLICATION_STATUS[key].label}</span>
                <strong>{stats.byStatus?.[key] || 0}</strong>
              </button>
            ))}
          </div>
          <div className="careers-insights">
            <span><i className="pi pi-bolt" aria-hidden="true" />{stats.last7Days} new in the last 7 days</span>
            <span><i className="pi pi-user-minus" aria-hidden="true" />{stats.unassignedOpen} open and unassigned</span>
            <span><i className="pi pi-briefcase" aria-hidden="true" />{stats.activeJobs} active job(s)</span>
          </div>
        </>
      )}

      <div className="careers-card is-flush">
        <div className="careers-filter-grid">
          <SearchBox value={searchText} onChange={setSearchText} placeholder="Search name, email or phone" label="Search applications" />
          <Dropdown
            value={filters.job_id || null}
            options={jobOptions}
            onChange={(e) => setFilters({ job_id: e.value })}
            placeholder="All jobs"
            showClear
            filter={jobOptions.length > 8}
            aria-label="Filter by job"
          />
          {categories.length > 0 && (
            <Dropdown
              value={filters.category_id || null}
              options={categories.map((c) => ({ label: c.name, value: String(c.category_id) }))}
              onChange={(e) => setFilters({ category_id: e.value })}
              placeholder="All categories"
              showClear
              aria-label="Filter by category"
            />
          )}
          <Dropdown
            value={filters.assigned_to || null}
            options={assigneeOptions}
            onChange={(e) => setFilters({ assigned_to: e.value })}
            placeholder="Assigned to anyone"
            showClear
            aria-label="Filter by assignee"
          />
          <Dropdown
            value={filters.min_rating || null}
            options={RATING_OPTIONS}
            onChange={(e) => setFilters({ min_rating: e.value })}
            placeholder="Any rating"
            showClear
            aria-label="Filter by rating"
          />
          <Calendar
            value={dateRange.length ? dateRange : null}
            onChange={(e) => {
              const [from, to] = e.value || [];
              // Wait for both ends of the range, or a cleared value
              if (!e.value || to) setFilters({ from: toDateOnly(from), to: toDateOnly(to) });
            }}
            selectionMode="range"
            readOnlyInput
            showButtonBar
            dateFormat="dd M yy"
            placeholder="Applied between…"
            aria-label="Filter by date applied"
          />
          <Dropdown
            value={filters.sort || 'newest'}
            options={SORT_OPTIONS}
            onChange={(e) => setFilters({ sort: e.value === 'newest' ? '' : e.value })}
            aria-label="Sort applications"
          />
        </div>

        {canManage && selected.length > 0 ? (
          <div className="careers-bulkbar">
            <span>{selected.length} selected</span>
            <Button
              label={`Change status (${selected.length})`}
              icon="pi pi-tag"
              severity="help"
              size="small"
              onClick={() => setBulkOpen(true)}
            />
            <Button label="Clear selection" text size="small" severity="secondary" onClick={() => setSelected([])} />
          </div>
        ) : hasFilters && (
          <div className="careers-table-footer border-top-0" style={{ borderBottom: '1px solid var(--c-border)' }}>
            <span>{loading ? 'Searching…' : `${total} matching ${total === 1 ? 'application' : 'applications'}`}</span>
            <Button
              label="Clear filters"
              icon="pi pi-filter-slash"
              text
              size="small"
              onClick={() => {
                setSearchText('');
                setParams({}, { replace: true });
              }}
            />
          </div>
        )}

        <ErrorBanner message={loadError} onRetry={load} />

        {loading && !items.length ? (
          <Loading />
        ) : !items.length && !loadError ? (
          <EmptyState icon="pi-inbox" title={hasFilters ? 'No applications match these filters' : 'No applications yet'}>
            {!hasFilters && <p className="mb-0 small">They appear here as candidates apply on the careers portal.</p>}
          </EmptyState>
        ) : (
          <div className="careers-table-wrap">
            <table className="careers-table">
              <thead>
                <tr>
                  {canManage && (
                    <th className="is-check">
                      <input
                        type="checkbox"
                        checked={allOnPageSelected}
                        onChange={toggleAll}
                        aria-label="Select all applications on this page"
                      />
                    </th>
                  )}
                  <th>Candidate</th>
                  <th>Job</th>
                  <th>Experience / CTC</th>
                  <th>Status</th>
                  <th>Rating</th>
                  <th>Assigned to</th>
                  <th>Applied</th>
                </tr>
              </thead>
              <tbody>
                {items.map((application) => {
                  const isSelected = selected.includes(application.application_id);
                  return (
                    <tr
                      key={application.application_id}
                      className={`careers-clickable-row ${isSelected ? 'is-selected' : ''}`}
                      onClick={() => navigate(applicationPath(application.application_id))}
                    >
                      {canManage && (
                        <td className="is-check" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleOne(application.application_id)}
                            aria-label={`Select ${application.name}`}
                          />
                        </td>
                      )}
                      <td style={{ minWidth: 240 }}>
                        <div className="careers-person">
                          <PersonAvatar name={application.name} />
                          <div className="text-truncate">
                            <span className="careers-primary-text">{application.name}</span>
                            <span className="careers-meta text-truncate">{application.email} · {application.phone}</span>
                          </div>
                        </div>
                      </td>
                      <td style={{ minWidth: 180 }}>
                        {application.job?.title || '—'}
                        <span className="careers-meta">{application.job?.category || ''}</span>
                      </td>
                      <td className="is-num">
                        {application.experience}
                        <span className="careers-meta">₹{application.expected_ctc} LPA</span>
                      </td>
                      <td><StatusBadge status={application.status} /></td>
                      <td className="careers-stars" aria-label={application.rating ? `${application.rating} out of 5` : 'Not rated'}>{stars(application.rating)}</td>
                      <td className="is-num">{application.assigned_to_name || <span className="text-muted">Unassigned</span>}</td>
                      <td className="is-num">{formatDate(application.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="careers-table-footer">
            <span>{total} applications</span>
            <Paginator
              first={(page - 1) * PAGE_SIZE}
              rows={PAGE_SIZE}
              totalRecords={total}
              onPageChange={(e) => setFilters({ page: e.page + 1 })}
            />
          </div>
        )}
      </div>

      <StatusChangeDialog
        visible={bulkOpen}
        title={`Change status of ${selected.length} application(s)`}
        statuses={BULK_STATUSES}
        busy={busy}
        onHide={() => setBulkOpen(false)}
        onSubmit={bulkUpdate}
      />
    </CareersPage>
  );
};

export default Applications;
